import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../../utils/supabaseClient";
import {
  selectIsMember,
  selectIsStaff,
  setIsAdmin,
  setIsMember,
  setIsStaff,
} from "../../store/slices/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectSettings } from "../../store/slices/settingsSlice";
import { useTheme } from "../../context/ThemeContext";
import { performLogout } from "../../utils/logout";

const Header = ({ transparent = false }) => {
  const location = useLocation();
  const pathParts = location.pathname.split("/").filter(Boolean);
  const tenantId = pathParts[0] || null;
  // STATE
  const [user, setUser] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // HELPER
  const dispatch = useDispatch();
  const settings = useSelector(selectSettings);
  const isStaff = useSelector(selectIsStaff);
  useSelector(selectIsMember);
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  useEffect(() => {
    const getSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    };

    getSession();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
      }
    );

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  const handleLogout = async () => {
    // Use centralized logout utility - clears all user data, preserves tenant info
    const tenantSlug = await performLogout();

    // Reset Redux state
    dispatch(setIsAdmin(false));
    dispatch(setIsStaff(false));
    dispatch(setIsMember(false));

    // Redirect to login (use preserved tenant slug or current tenantId)
    navigate(`/${tenantSlug || tenantId}/login`);
  };


  return (
    <nav
      className={`w-full z-30 ${transparent ? "bg-transparent" : "bg-[var(--color-primary)]"} text-[var(--color-primary-text)]`}
    >
      <div className="container mx-auto px-4 sm:px-6 py-3 flex justify-between items-center relative">
        {/* Left-side links for desktop */}
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-6">
            {/* Logo */}
            <div className="flex items-center space-x-4">
              <Link to={`/${tenantId}`} className="flex items-center">
                {settings?.logo_url ? (
                  <img
                    src={settings.logo_url}
                    alt={`${settings?.business_name || "Club"} logo`}
                    className="h-20 w-auto rounded-md object-contain"
                  />
                ) : (
                  <span
                    className="text-xl font-bold"
                    // style={{ color: settings?.secondary_color || "rgb(31 41 55 / var(--tw-text-opacity, 1))" }}
                    style={{ color: "#ffffff" }}
                  >
                    {settings?.business_name || "Club"}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </div>


        {/* Right-side elements (contact and language) */}
        <div className="flex items-center space-x-6">
          {/* Desktop */}
          <div className="hidden md:flex space-x-8">
            <Link
              to={`/${tenantId}`}
              className="text-white hover:text-gray-300"
            >
              Home
            </Link>
            <Link
              to={`/${tenantId}/programs`}
              className="text-white hover:text-gray-300"
            >
              Programs
            </Link>
            <Link
              to={`/${tenantId}/about`}
              className="text-white hover:text-gray-300"
            >
              About
            </Link>
            <Link
              to={`/${tenantId}/contact`}
              className="text-white hover:text-gray-300"
            >
              Contact
            </Link>

            {user && (
              <div className="relative group inline-block">
                <div className="flex items-center space-x-1 focus:outline-none cursor-pointer">
                  <span className="text-white hover:text-gray-300">
                    My Account
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>

                <div className={`absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 z-50 opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-all duration-200 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                  <Link
                    to={
                      isStaff
                        ? `/${tenantId}/staff/view-invoice`
                        : `/${tenantId}/member/view-invoice`
                    }
                    className={`block px-4 py-2 text-sm ${isDarkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to={`/${tenantId}/profile`}
                    className={`block px-4 py-2 text-sm ${isDarkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
                  >
                    Profile
                  </Link>
                  <button
                    onClick={handleLogout}
                    className={`block w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Button */}
          {!user ? (
            <div className="hidden md:flex items-center space-x-4">
              <Link
                to={`/${tenantId}/login`}
                className="px-4 py-2 rounded-md transition-colors duration-200"
                style={{
                  backgroundColor: '#ffffff',
                  color: isDarkMode ? '#4b5563' : '#6b7280'
                }}
              >
                Sign In
              </Link>
              <Link
                to={`/${tenantId}/register`}
                className="px-4 py-2 rounded-md transition-colors duration-200"
                style={{
                  backgroundColor: isDarkMode ? '#4b5563' : '#6b7280',
                  color: '#ffffff'
                }}
              >
                Register
              </Link>
            </div>
          ) : (
            <div className="hidden md:block relative group inline-block">
              <div className="flex items-center space-x-1 focus:outline-none cursor-pointer">
                <span className="text-white hover:text-gray-300">
                  My Account
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              <div className={`absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 z-50 opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-all duration-200 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <Link
                  to={
                    isStaff
                      ? `/${tenantId}/staff/view-invoice`
                      : `/${tenantId}/member/view-invoice`
                  }
                  className="block px-4 py-2 text-sm transition-colors duration-200"
                  style={{
                    color: isDarkMode ? '#9ca3af' : '#6b7280',
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isDarkMode ? '#374151' : '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = isDarkMode ? '#9ca3af' : '#6b7280';
                  }}
                >
                  Dashboard
                </Link>
                <Link
                  to={`/${tenantId}/profile`}
                  className="block px-4 py-2 text-sm transition-colors duration-200"
                  style={{
                    color: isDarkMode ? '#9ca3af' : '#6b7280',
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isDarkMode ? '#374151' : '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = isDarkMode ? '#9ca3af' : '#6b7280';
                  }}
                >
                  Profile
                </Link>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-sm transition-colors duration-200"
                  style={{
                    backgroundColor: isDarkMode ? '#4b5563' : '#6b7280',
                    color: '#ffffff'
                  }}
                >
                  Sign Out
                </button>
              </div>
            </div>
          )}
          
          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              className={`outline-none mobile-menu-button p-2 rounded-md transition-colors duration-200 ${
                isDarkMode
                  ? 'bg-gray-700 bg-opacity-50 hover:bg-opacity-70 border border-gray-800'
                  : 'bg-white bg-opacity-10 hover:bg-opacity-20 border border-white border-opacity-30'
              }`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle mobile menu"
            >
              <svg
                className={`w-6 h-6 transition-transform duration-200 ${isMobileMenuOpen ? 'rotate-90' : ''}`}
                style={{
                  color: '#ffffff'
                }}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isMobileMenuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile menu */}
      <div className={`mobile-menu md:hidden transition-all duration-300 ease-in-out ${isMobileMenuOpen ? 'block' : 'hidden'}`}>
        <ul className="px-4 py-2 shadow-lg bg-[var(--color-primary)]">
          <li>
            <Link
              to={`/${tenantId}`}
              className={`block px-4 py-3 text-white rounded-md transition-colors duration-200 ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-black hover:bg-opacity-20'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Home
            </Link>
          </li>
          <li>
            <Link
              to={`/${tenantId}/programs`}
              className={`block px-4 py-3 text-white rounded-md transition-colors duration-200 ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-black hover:bg-opacity-20'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Programs
            </Link>
          </li>
          <li>
            <Link
              to={`/${tenantId}/about`}
              className={`block px-4 py-3 text-white rounded-md transition-colors duration-200 ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-black hover:bg-opacity-20'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              About
            </Link>
          </li>
          <li>
            <Link
              to={`/${tenantId}/contact`}
              className={`block px-4 py-3 text-white rounded-md transition-colors duration-200 ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-black hover:bg-opacity-20'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Contact
            </Link>
          </li>
          {user ? (
            <>
              <li>
                <Link
                  to={
                    isStaff
                      ? `/${tenantId}/staff/view-invoice`
                      : `/${tenantId}/member/view-invoice`
                  }
                  className={`block px-4 py-3 text-white rounded-md transition-colors duration-200 ${
                    isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-black hover:bg-opacity-20'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
              </li>
              <li>
                <Link
                  to={`/${tenantId}/profile`}
                  className={`block px-4 py-3 text-white rounded-md transition-colors duration-200 ${
                    isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-black hover:bg-opacity-20'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Profile
                </Link>
              </li>
              <li>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className={`block w-full text-left px-4 py-3 text-white rounded-md transition-colors duration-200 ${
                    isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-black hover:bg-opacity-20'
                  }`}
                >
                  Sign Out
                </button>
              </li>
            </>
          ) : (
            <>
              <li>
                <Link
                  to={`/${tenantId}/login`}
                  className={`block px-4 py-3 text-white rounded-md transition-colors duration-200 ${
                    isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-black hover:bg-opacity-20'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sign In
                </Link>
              </li>
              <li>
                <Link
                  to={`/${tenantId}/register`}
                  className={`block px-4 py-3 text-white rounded-md transition-colors duration-200 ${
                    isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-black hover:bg-opacity-20'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Register
                </Link>
              </li>
            </>
          )}
        </ul>
      </div>
    </nav>
  );
};

export default Header;
